if (!customElements.get('noble-origins-plans')) {
  customElements.define(
    'noble-origins-plans',
    class extends HTMLElement {
      connectedCallback() {
        this.plans = this.readPlans();
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-purchase-input], [data-selling-plan]')) this.sync();
        });
        document.addEventListener('noble-origins:variant-change', (event) => {
          const section = this.closest('[data-section-id]');
          const source = event.target.closest?.('[data-section-id]');
          if (section && source && section !== source) return;
          if (event.detail?.variant?.id) this.variantId = event.detail.variant.id;
          this.sync();
        });
        this.applyPlanFromUrl();
        if (!this.querySelector('[data-purchase-input]:checked')) {
          const first = this.querySelector('[data-purchase-input]');
          if (first) first.checked = true;
        }
        this.sync();
      }

      applyPlanFromUrl() {
        const planId = new URL(window.location.href).searchParams.get('selling_plan');
        if (!planId) return;
        const planInput = this.querySelector(`[data-selling-plan="${planId}"]`);
        if (!planInput) return;
        planInput.checked = true;
        const purchaseInput = planInput.closest('[data-purchase]')?.querySelector('[data-purchase-input]');
        if (purchaseInput) purchaseInput.checked = true;
      }

      writePlanUrl(sellingPlanId) {
        const url = new URL(window.location.href);
        if (sellingPlanId) url.searchParams.set('selling_plan', sellingPlanId);
        else url.searchParams.delete('selling_plan');
        window.history.replaceState({}, '', url);
      }

      readPlans() {
        try {
          return JSON.parse(this.querySelector('[data-plans]')?.textContent || '{}');
        } catch (error) {
          return {};
        }
      }

      currentVariant() {
        const variants = this.plans.variants || [];
        const id = this.variantId || this.closest('[data-section-id]')?.querySelector('[name="id"]')?.value;
        return variants.find((variant) => String(variant.id) === String(id)) || variants[0];
      }

      allocationFor(variant, planId) {
        if (!variant || planId == null || planId === '') return null;
        return variant.plans?.find((plan) => String(plan.id) === String(planId)) || null;
      }

      recurringAdjustment(adjustments) {
        const items = adjustments || [];
        return items.find((item) => item.order_count == null) || items[0] || null;
      }

      fillPlanList(template, allocation, fallback) {
        const recurring = this.recurringAdjustment(allocation?.adjustments);
        if (recurring?.value_type === 'price') {
          const price = this.formatMoney(recurring.value);
          return template
            .replaceAll('[percentage] off recurring', `${price} recurring`)
            .replaceAll('[percentage]', price)
            .replaceAll('[amount]', price);
        }
        const percentage = recurring?.value_type === 'percentage'
          ? Math.round(Number(recurring.value))
          : fallback.percentage;
        const listSavings = Math.round(((fallback.base || 0) * percentage) / 100);
        return template
          .replaceAll('[percentage]', `${percentage}%`)
          .replaceAll('[amount]', this.formatMoney(listSavings));
      }

      retailCents(variant) {
        const value = Number(variant?.retail) || 0;
        if (value <= 0) return 0;
        if (value < 1000) return Math.round(value * 100);
        return value;
      }

      compareFor(variant, allocation) {
        if (!allocation) return 0;
        const retail = this.retailCents(variant);
        if (retail > allocation.price) return retail;
        if (allocation.compare > allocation.price) return allocation.compare;
        if (variant && variant.price > allocation.price) return variant.price;
        return 0;
      }

      syncFrequencies(variant) {
        const planIds = new Set((variant?.plans || []).map((plan) => String(plan.id)));
        this.querySelectorAll('[data-purchase="subscription"]').forEach((card) => {
          const inputs = [...card.querySelectorAll('[data-selling-plan]')];
          const checkedLabel = inputs
            .find((input) => input.checked)
            ?.closest('.noble-origins-plan__frequency-option')
            ?.querySelector('span')
            ?.textContent
            ?.trim();

          inputs.forEach((input) => {
            const option = input.closest('.noble-origins-plan__frequency-option');
            const available = planIds.has(String(input.value));
            if (option) option.hidden = !available;
            if (!available) input.checked = false;
          });

          const visible = inputs.filter((input) => planIds.has(String(input.value)));
          if (visible.length && !visible.some((input) => input.checked)) {
            const sameFrequency = checkedLabel
              ? visible.find((input) => {
                  return input.closest('.noble-origins-plan__frequency-option')?.querySelector('span')?.textContent?.trim() === checkedLabel;
                })
              : null;
            (sameFrequency || visible[0]).checked = true;
          }

          const frequency = card.querySelector('.noble-origins-plan__frequency');
          if (frequency) frequency.hidden = visible.length === 0;
        });
      }

      syncOneTimeAvailability(variant) {
        const card = this.querySelector('[data-purchase="one-time"]');
        if (!card) return;
        const hide = Boolean(variant?.hideOneTime);
        card.hidden = hide;
        if (!hide) return;
        const input = card.querySelector('[data-purchase-input]');
        if (input) input.checked = false;
      }

      syncSubscriptionAvailability(variant) {
        const hasPlans = (variant?.plans || []).length > 0;
        const subscriptions = this.querySelector('.noble-origins-plans__subscriptions');
        if (subscriptions) subscriptions.hidden = !hasPlans;
        this.querySelectorAll('[data-purchase="subscription"]').forEach((card) => {
          card.hidden = !hasPlans;
          if (!hasPlans) {
            const input = card.querySelector('[data-purchase-input]');
            if (input) input.checked = false;
          }
        });
        if (!this.querySelector('[data-purchase-input]:checked')) {
          const subscription = this.querySelector('[data-purchase="subscription"]:not([hidden]) [data-purchase-input]');
          const oneTime = this.querySelector('[data-purchase="one-time"]:not([hidden]) [data-purchase-input]');
          const next = subscription || oneTime;
          if (next) next.checked = true;
        }
      }

      sync() {
        const variant = this.currentVariant();
        this.syncFrequencies(variant);
        this.syncOneTimeAvailability(variant);
        this.syncSubscriptionAvailability(variant);
        const selectedInput = this.querySelector('[data-purchase-input]:checked');
        const selectedCard = selectedInput?.closest('[data-purchase]');

        this.querySelectorAll('[data-purchase]').forEach((card) => {
          card.classList.toggle('is-selected', Boolean(card.querySelector('[data-purchase-input]:checked')));
        });
        this.querySelectorAll('[data-selling-plan]').forEach((input) => {
          input.closest('.noble-origins-plan__frequency-option')?.classList.toggle('is-selected', input.checked);
        });

        this.querySelectorAll('[data-purchase="subscription"]').forEach((card) => {
          const planInput = card.querySelector('[data-selling-plan]:checked');
          const allocation = this.allocationFor(variant, planInput?.value);
          const price = allocation ? allocation.price : variant?.price || 0;
          const compare = this.compareFor(variant, allocation);
          const priceEl = card.querySelector('[data-subscription-price]');
          if (priceEl) priceEl.textContent = this.formatMoney(price);
          const compareEl = card.querySelector('[data-subscription-compare]');
          if (compareEl) {
            if (compare > price) {
              compareEl.hidden = false;
              compareEl.textContent = this.formatMoney(compare);
            } else {
              compareEl.hidden = true;
              compareEl.textContent = '';
            }
          }
          const savings = Math.max(0, compare - price);
          const percentage = compare > price ? Math.round((savings / compare) * 100) : 0;
          const saveBadge = card.querySelector('[data-save-badge]');
          if (saveBadge) {
            const template = saveBadge.dataset.saveBadge || '';
            saveBadge.hidden = savings <= 0;
            saveBadge.textContent = template
              .replaceAll('[percentage]', `${percentage}%`)
              .replaceAll('[amount]', this.formatMoney(savings));
          }
          const valueBadge = card.querySelector('[data-value-badge]');
          if (valueBadge) {
            const text = variant?.valueBadge || '';
            valueBadge.hidden = text === '';
            valueBadge.textContent = text;
          }
          const planList = card.querySelector('[data-plan-list]');
          if (planList) {
            planList.innerHTML = this.fillPlanList(planList.dataset.planList || '', allocation, {
              percentage,
              base: compare || variant?.price || 0,
            });
          }
        });
        this.writePrice('[data-one-time-price]', variant?.price || 0);

        const selectedPlan = selectedCard?.querySelector('[data-selling-plan]:checked');
        const allocation = this.allocationFor(variant, selectedPlan?.value);
        const usePlan = selectedInput?.value === 'subscription' && allocation;
        const buttonPrice = usePlan ? allocation.price : variant?.price || 0;
        const sellingPlanId = usePlan ? String(allocation.id) : '';
        const section = this.closest('[data-section-id]');
        const sellingPlanInput = section?.querySelector('[data-selling-plan-input]');
        if (sellingPlanInput) sellingPlanInput.value = sellingPlanId;
        this.writePlanUrl(sellingPlanId);

        this.dispatchEvent(
          new CustomEvent('noble-origins:plan-change', {
            bubbles: true,
            detail: { price: buttonPrice, sellingPlanId },
          })
        );
      }

      writePrice(selector, cents) {
        const element = this.querySelector(selector);
        if (element) element.textContent = this.formatMoney(cents);
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const amount = (Number(cents) / 100).toFixed(2);
        const noDecimals = String(Math.round(Number(cents) / 100));
        return format
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, noDecimals)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
          .replace(/\{\{\s*amount\s*\}\}/, amount);
      }
    }
  );
}
